import React, { useEffect, useState } from "react";
import {
  X,
  Calendar,
  MapPin,
  FileText,
  Loader2,
  DollarSign,
  Plane,
  Paperclip,
} from "lucide-react";
import { createBusinessTrip } from "../../../api/hrm/business_trips/businessTrip";
import {
  showSuccessToast,
  showErrorToast,
} from "../../../components/ConfirmDialog";
import { AnimatePresence, motion } from "framer-motion";

const labelClass =
  "text-[11px] font-bold text-gray-400 uppercase tracking-widest ml-1";
const inputClass =
  "w-full bg-white border border-gray-300 rounded-2xl px-4 py-3 text-sm font-medium text-gray-800 outline-none transition-all hover:border-gray-400 focus:bg-white focus:border-red-500 focus:ring-4 focus:ring-red-500/5";

const BusinessTripCreateModal = ({ isOpen, onClose, onSaved }) => {
  const storedUser = localStorage.getItem("user");
  const user = storedUser ? JSON.parse(storedUser) : null;

  const [loading, setLoading] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [budgetCost, setBudgetCost] = useState("");
  const [location, setLocation] = useState("");
  const [reason, setReason] = useState("");
  const [attachedFile, setAttachedFile] = useState(null);

  useEffect(() => {
    if (isOpen) {
      const now = new Date().toISOString().split("T")[0];
      setStartDate(now);
      setEndDate(now);
      setLocation("");
      setReason("");
      setBudgetCost("");
      setAttachedFile(null);
    }
  }, [isOpen]);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.type !== "application/pdf") {
      showErrorToast("Chỉ được chọn file PDF!");
      e.target.value = null;
      return;
    }
    if (file.size > 25 * 1024 * 1024) {
      showErrorToast("File vượt quá 25MB!");
      e.target.value = null;
      return;
    }

    setAttachedFile(file);
  };

  const handleSave = async () => {
    if (!user?.id || !startDate || !endDate || !reason.trim() || !location.trim()) {
      showErrorToast("Vui lòng nhập đầy đủ thông tin bắt buộc");
      return;
    }

    if (endDate < startDate) {
      showErrorToast("Ngày kết thúc phải sau ngày bắt đầu");
      return;
    }

    try {
      setLoading(true);

      const formData = new FormData();
      formData.append("employee_id", user.id);
      formData.append("start_date", startDate);
      formData.append("end_date", endDate);
      formData.append("budget_cost", budgetCost || 0);
      formData.append("location", location.trim());
      formData.append("reason", reason.trim());
      if (attachedFile) formData.append("attached_file", attachedFile);

      await createBusinessTrip(formData);

      showSuccessToast("Đăng ký công tác thành công!");
      onSaved?.();
      onClose();
    } catch (error) {
      console.error("Lỗi khi đăng ký công tác:", error);
      showErrorToast("Không thể đăng ký công tác");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[200] bg-black/45 backdrop-blur-sm"
            onClick={onClose}
          />

          <div className="fixed inset-0 z-[210] flex items-end justify-center p-0 pointer-events-none sm:items-center sm:p-4">
            <motion.div
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{ type: "spring", damping: 30, stiffness: 320 }}
              className="pointer-events-auto flex h-[90dvh] w-full max-w-3xl flex-col overflow-hidden rounded-t-[1.75rem] border border-gray-200 bg-white shadow-2xl sm:h-auto sm:max-h-[90vh] sm:rounded-[2rem]"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex shrink-0 items-start justify-between gap-3 border-b border-gray-200 bg-gradient-to-r from-red-50/70 via-white to-white px-5 py-4 sm:px-6">
                <div className="flex min-w-0 items-center gap-3 sm:gap-4">
                  <div className="rounded-2xl border border-red-700/10 bg-red-700/5 p-3 text-red-700 shadow-sm">
                    <Plane className="h-5 w-5 sm:h-6 sm:w-6" strokeWidth={2.25} />
                  </div>
                  <div className="min-w-0">
                    <h2 className="text-lg font-black uppercase tracking-tight text-gray-800 sm:text-xl">
                      Đăng ký <span className="text-red-700">công tác</span>
                    </h2>
                    <p className="mt-0.5 text-[10px] font-bold uppercase tracking-[0.16em] text-gray-400">
                      Thời gian, địa điểm và lý do công tác
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded-xl border border-gray-200 bg-white p-2 text-gray-500 transition hover:border-red-200 hover:bg-red-50 hover:text-red-600"
                  aria-label="Đóng"
                >
                  <X size={18} />
                </button>
              </div>

              <div className="min-h-0 flex-1 space-y-5 overflow-y-auto overscroll-contain px-5 py-5 sm:px-6">
                <section className="space-y-4 rounded-2xl border border-gray-200 bg-gradient-to-br from-gray-50/80 to-white p-4 sm:p-5">
                  <h3 className="text-[11px] font-black uppercase tracking-widest text-gray-500">
                    Thời gian & địa điểm
                  </h3>

                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                      <label className={`${labelClass} mb-1.5 flex items-center gap-1.5`}>
                        <Calendar size={12} className="text-red-500" />
                        Ngày bắt đầu <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        className={inputClass}
                      />
                    </div>
                    <div>
                      <label className={`${labelClass} mb-1.5 flex items-center gap-1.5`}>
                        <Calendar size={12} className="text-red-500" />
                        Ngày kết thúc <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        className={inputClass}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                      <label className={`${labelClass} mb-1.5 flex items-center gap-1.5`}>
                        <DollarSign size={12} className="text-red-500" />
                        Ngân sách dự kiến (VNĐ)
                      </label>
                      <input
                        type="text"
                        inputMode="numeric"
                        value={
                          budgetCost
                            ? Number(budgetCost).toLocaleString("vi-VN")
                            : ""
                        }
                        onChange={(e) => {
                          const raw = e.target.value.replace(/\D/g, "");
                          setBudgetCost(raw ? parseInt(raw, 10) : "");
                        }}
                        className={inputClass}
                        placeholder="0"
                      />
                    </div>
                    <div>
                      <label className={`${labelClass} mb-1.5 flex items-center gap-1.5`}>
                        <MapPin size={12} className="text-red-500" />
                        Địa điểm <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={location}
                        onChange={(e) => setLocation(e.target.value)}
                        className={inputClass}
                        placeholder="VD: Hà Nội, Đà Nẵng..."
                      />
                    </div>
                  </div>
                </section>

                <section className="space-y-4 rounded-2xl border border-gray-200 bg-gradient-to-br from-gray-50/80 to-white p-4 sm:p-5">
                  <h3 className="text-[11px] font-black uppercase tracking-widest text-gray-500">
                    Lý do & chứng từ
                  </h3>

                  <div>
                    <label className={`${labelClass} mb-1.5 flex items-center gap-1.5`}>
                      <FileText size={12} className="text-red-500" />
                      Lý do công tác <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      rows={3}
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      className={`${inputClass} resize-none`}
                      placeholder="Mục tiêu, nội dung công tác..."
                      maxLength={250}
                    />
                    <p
                      className={`mt-1 text-right text-[11px] ${
                        reason.length > 200
                          ? "font-bold text-red-500"
                          : "text-gray-400"
                      }`}
                    >
                      {reason.length}/250
                    </p>
                  </div>

                  <div>
                    <label className={`${labelClass} mb-1.5 flex items-center gap-1.5`}>
                      <Paperclip size={12} className="text-red-500" />
                      File đính kèm (PDF)
                    </label>
                    <label
                      className="flex cursor-pointer flex-col items-center justify-center rounded-2xl border-2 border-dashed border-gray-300 bg-white px-4 py-8 text-center transition hover:border-red-300 hover:bg-red-50/30"
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={(e) => {
                        e.preventDefault();
                        const file = e.dataTransfer.files?.[0];
                        if (file) {
                          handleFileSelect({ target: { files: [file] } });
                        }
                      }}
                    >
                      <div className="mb-2 flex h-10 w-10 items-center justify-center rounded-xl bg-red-50 text-red-700">
                        <FileText size={18} />
                      </div>
                      <span className="text-sm font-bold text-red-700">
                        Chọn tệp PDF để tải lên
                      </span>
                      <span className="mt-1 text-[11px] text-gray-400">
                        Kéo thả hoặc nhấn để chọn · tối đa 25 MB
                      </span>
                      <input
                        type="file"
                        accept="application/pdf"
                        className="hidden"
                        onChange={handleFileSelect}
                      />
                    </label>
                    {attachedFile ? (
                      <div className="mt-2 flex items-center gap-2 rounded-xl border border-emerald-100 bg-emerald-50/70 px-3 py-2 text-sm text-emerald-800">
                        <Paperclip size={14} className="shrink-0" />
                        <span className="min-w-0 flex-1 truncate font-medium">
                          {attachedFile.name}
                        </span>
                        <span className="shrink-0 text-[11px] text-emerald-700/70">
                          {(attachedFile.size / 1024 / 1024).toFixed(1)} MB
                        </span>
                        <button
                          type="button"
                          onClick={() => setAttachedFile(null)}
                          className="shrink-0 text-[11px] font-bold text-rose-600 underline"
                        >
                          Xóa
                        </button>
                      </div>
                    ) : null}
                  </div>
                </section>
              </div>

              <div className="flex shrink-0 flex-col-reverse gap-2 border-t border-gray-100 bg-gray-50/80 px-5 py-4 sm:flex-row sm:justify-end sm:gap-3 sm:px-6">
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded-full border border-gray-200 bg-white px-6 py-2.5 text-xs font-bold uppercase tracking-wider text-gray-600 transition hover:border-gray-300 hover:bg-gray-50"
                >
                  Hủy
                </button>
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={loading}
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-red-700 to-red-800 px-6 py-2.5 text-xs font-black uppercase tracking-wider text-white shadow-md shadow-red-100 transition hover:shadow-lg disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Đang lưu...
                    </>
                  ) : (
                    "Gửi đăng ký"
                  )}
                </button>
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
};

export default BusinessTripCreateModal;
